import classnames from 'classnames'
import React from 'react'
import {UncontrolledTooltip} from 'reactstrap'
import {useSelectedVariantContext} from '../context/SelectedVariantContext'
import {replaceUnderline} from '../context/SelectedVariantContext/helper'
import {actionChangeTabDesign, updateActiveSide} from '../context/SelectedVariantContext/action'
import {useEditorSides} from '../../../hooks/useEditorSides'
import {isBackofficeEnv} from '../../../helpers/isBackoffceEnv'

const SwitchBetweenSides = () => {
    const {
        state: {selectedSide, display, runningImageCompress, isAdmin, productCreatedType, cropMode, variants, product},
        dispatch,
    } = useSelectedVariantContext()
    const { sides } = useEditorSides()

    const tooltipBySide = React.useMemo(() => {
        const hasMockups = !!(product?.mockups && product.mockups.length > 0)
        if (hasMockups) return {}

        const rawVariants = Array.isArray(variants) ? variants : []

        const normalizedVariants = rawVariants.map((variant) => {
            const attributes = {}
            ;(variant.attributes || []).forEach((attr) => {
                if (!attr?.type) return
                attributes[attr.type] = attr.value_text ?? attr.value_code
            })

            const dimensions = {}
            ;(variant.dimensions || []).forEach((d) => {
                Object.keys(d || {}).forEach((side) => {
                    dimensions[side] = true
                })
            })

            return {attributes, dimensions}
        })

        const allValuesByType = {}
        normalizedVariants.forEach((v) => {
            Object.entries(v.attributes).forEach(([type, value]) => {
                if (!allValuesByType[type]) allValuesByType[type] = new Set()
                allValuesByType[type].add(value)
            })
        })

        const result = {}

        ;(sides || []).forEach((sideKey) => {
            const supportedVariants = normalizedVariants.filter((v) => v.dimensions?.[sideKey])

            if (supportedVariants.length === normalizedVariants.length) {
                result[sideKey] = 'Support all variants'
                return
            }

            if (supportedVariants.length === 0) {
                return
            }

            const constraints = []

            Object.keys(allValuesByType).forEach((type) => {
                const valueToSupportMap = {}

                allValuesByType[type].forEach((value) => {
                    valueToSupportMap[value] = false
                })

                normalizedVariants.forEach((variant) => {
                    if (variant.attributes[type] == null) return
                    if (variant.dimensions?.[sideKey]) {
                        valueToSupportMap[variant.attributes[type]] = true
                    }
                })

                const valuesWithSupport = Object.entries(valueToSupportMap)
                    .filter(([, hasSupport]) => hasSupport)
                    .map(([value]) => value)

                const valuesWithoutSupport = Object.entries(valueToSupportMap).filter(([, hasSupport]) => !hasSupport)

                if (valuesWithSupport.length > 0 && valuesWithoutSupport.length > 0) {
                    constraints.push(`${replaceUnderline(type)} ${valuesWithSupport.join(', ')}`)
                }
            })

            if (constraints.length === 0) {
                result[sideKey] = 'Support all variants'
            } else if (constraints.length === 1) {
                result[sideKey] = `Only support for ${constraints[0]}`
            } else {
                result[sideKey] = `Only support for: ${constraints.join(' and ')}`
            }
        })

        return result
    }, [variants, sides, product])

    const shouldShowSideTooltips = !product?.mockups || product.mockups.length === 0
    const getSideBtnId = (sideType) => `switch_side_${String(sideType).replace(/[^a-zA-Z0-9_-]/g, '_')}`

    const changeSide = async (tab) => {
        if (tab === selectedSide) return
        dispatch(updateActiveSide(tab))
        dispatch(actionChangeTabDesign(''))
    }

    const switchBtnStyles = 'Side text-base text-text-primary border-gray-300'

    return (
        <div className="SwitchBetweenSides">
            {display === 'design' && (
                <div className="Sides" id={'switch_sides'}>
                    {sides?.map((sideType) => (
                        <React.Fragment key={sideType}>
                            <button
                                id={getSideBtnId(sideType)}
                                type="button"
                                className={classnames(switchBtnStyles, {
                                    active: selectedSide === sideType,
                                    'text-white': selectedSide === sideType,
                                })}
                                onClick={() => {
                                    changeSide(sideType)
                                }}
                                disabled={runningImageCompress || cropMode}
                            >
                                {replaceUnderline(sideType)}
                            </button>
                            {shouldShowSideTooltips && tooltipBySide?.[sideType] && (
                                <UncontrolledTooltip autohide={false} placement="top" target={getSideBtnId(sideType)}>
                                    {tooltipBySide?.[sideType]}
                                </UncontrolledTooltip>
                            )}
                        </React.Fragment>
                    ))}

                    {productCreatedType !== 'regularNoTemplate' &&
                        productCreatedType !== 'type2D' &&
                        productCreatedType !== 'combineLayout' && (
                            <>
                                <button
                                    type="button"
                                    className={classnames(`${switchBtnStyles} AllAreas`, {
                                        active: selectedSide === 'All',
                                    })}
                                    onClick={() => {
                                        changeSide('All')
                                    }}
                                    id="all_areas"
                                    disabled={runningImageCompress || cropMode}
                                >
                                    All Areas (view only)
                                </button>

                                {isAdmin && isBackofficeEnv() && (
                                    <button
                                        type="button"
                                        className={classnames(`${switchBtnStyles} `, {
                                            active: selectedSide === 'Safezone',
                                        })}
                                        onClick={() => {
                                            changeSide('Safezone')
                                        }}
                                        disabled={runningImageCompress}
                                    >
                                        Safe zone
                                    </button>
                                )}
                            </>
                        )}
                </div>
            )}
        </div>
    )
}

export default SwitchBetweenSides
